import { EMPTY_TROOPS } from "../logistics-math.js";
import { UNIT_IDS } from "../logistics-types.js";
import type { SeatObservation } from "../types.js";
import type { AppState } from "./app-source.js";
import { compactTroops, seatName } from "./border-situation.js";
import { help } from "./layout-view.js";
import { type AppNode, gameIntent, text } from "./view-nodes.js";
import { waveTimingControls } from "./wave-timing-view.js";

export function productionRouting(
  o: SeatObservation,
  preview?: Readonly<Record<string, number>>,
): AppNode {
  const shares = preview ?? o.self.logistics.reinforcement?.shares["guard-robot"] ?? {};
  return {
    kind: "row",
    id: "production-routing",
    children: [
      text(
        "army-share-total",
        `${Object.keys(o.self.logistics.borders)
          .map((id) => `${seatName(o, id)}: ${shares[id] ?? 0}%`)
          .join(
            " · ",
          )} · Home: ${100 - Object.values(shares).reduce((a, b) => a + b, 0)}% · Total: 100%`,
      ),
      text(
        "reserve-production-shares",
        `Keep at home: ${100 - Object.values(shares).reduce((a, b) => a + b, 0)}% · ${compactTroops(o.self.units)}`,
      ),
      help(
        "routing-help",
        "Army split",
        "Army shares split all troops still at home and future production. Changing shares rebalances waiting waves. Deployed and traveling troops stay committed. Unassigned share stays home.",
      ),
    ],
  };
}

export function routingSliders(
  o: SeatObservation,
  id: string,
  preview?: Readonly<Record<string, number>>,
): AppNode {
  const shares = preview ?? o.self.logistics.reinforcement?.shares["guard-robot"] ?? {};
  return {
    kind: "range-input",
    id: `army-share-${id}`,
    label: "Army share",
    value: shares[id] ?? 0,
    min: 0,
    max: 100,
    step: 1,
    tooltip: "Share of troops at home and future production, across all unit types.",
    previewIntent: (value) => ({ kind: "preview-army-share", borderId: id, value }),
    cancelIntent: { kind: "cancel-army-preview" },
    intent: (value) =>
      gameIntent(`army-share-${id}`, {
        kind: "adjust-army",
        borderId: id,
        field: "share",
        value,
      }),
  };
}

export { rebalanceShares } from "../army-shares.js";

export function reinforcementControls(state: AppState, id: string): AppNode {
  const o = state.observation as SeatObservation;
  const troops = o.self.logistics.reinforcement?.gathered[id] ?? EMPTY_TROOPS;
  const empty = UNIT_IDS.every((unit) => troops[unit] === "0");
  const launchId = `launch-${id}`;
  return {
    kind: "stack",
    id: `reinforcement-${id}`,
    style: { className: "kf-wave-controls" },
    children: [
      ...waveTimingControls(o, id),
      text(`gathered-${id}`, `Next wave: ${compactTroops(troops)}`),
      {
        kind: "action",
        id: launchId,
        action: {
          id: launchId,
          label: `Send now to ${seatName(o, id)}`,
          tooltip: "Send this front’s waiting wave. An empty wave waits for more troops.",
          enabled: !empty,
          intent: gameIntent(launchId, { kind: "launch-wave", borderId: id }),
          blockers: empty
            ? [
                {
                  kind: "disabled",
                  actionId: launchId,
                  reasonKey: "Wave empty. Waiting for troops.",
                },
              ]
            : [],
        },
      },
    ],
  };
}
